import { ActionType, ProTable } from "@ant-design/pro-components";
import { Button, Select, Spin, Tag, message } from "antd";
import { useGlobalState } from "../../components/global";
import { useEffect, useRef, useState } from "react";
import UUIDComponent from "../../components/UUIDComponent";
import { businessType } from "../const";
import PulseResult from "./PulseResult";
import TongueDiagnosisResult from "./TongueDiagnosisResult";
import ConstitutionResult from "./ConstitutionResult";
import FoodRecognitionResult from "./FoodRecognitionResult";

export default function ResultFeedback() {
    const { jsonRequest } = useGlobalState();
    const tableRef = useRef<ActionType>();
    const [dataList, setDataList] = useState<any[]>([]);

    useEffect(() => {
        tableRef.current?.reload();
    }, []);

    const columns: any[] = [
        {
            title: "ID",
            dataIndex: "id",
            align: "center",
            search: false,
            render: (value: any) => <UUIDComponent value={value} />
        },
        {
            title: "用户id",
            dataIndex: "userId",
            align: "center",
            search: false,
            render: (value: any) => <UUIDComponent value={value} />
        },
        {
            title: "反馈类型",
            dataIndex: "likeFlag",
            align: "center",
            search: true,
            render: (text: boolean) => {
                return text ? <Tag color="green">点赞</Tag> : <Tag color="red">点踩</Tag>;
            },
            renderFormItem: () => (
                <Select
                    allowClear
                    placeholder='请选择反馈类型'
                    options={[
                        { label: "点赞", value: true },
                        { label: "点踩", value: false },
                    ]}
                />
            ),
        },
        {
            title: "业务id",
            dataIndex: "businessId",
            align: "center",
            search: false,
            render: (value: any) => <UUIDComponent value={value} />
        },
        {
            title: "业务类型",
            dataIndex: "businessType",
            align: "center",
            search: true,
            valueEnum: businessType,
        },
        {
            title: "标签",
            dataIndex: "labels",
            align: "center",
            search: false,
            render: (text: any) => {
                if (Array.isArray(text)) {
                    return (
                        <>
                            {text.map((item: string, index: number) => (
                                <Tag key={index} style={{ margin: '2px 4px' }}
                                    color="blue">{item}</Tag>
                            ))}
                        </>
                    );
                }
                else if (typeof text === 'string' && text) {
                    return text;
                }
                return null;
            }
        },
        {
            title: "反馈内容",
            dataIndex: "content",
            align: "center",
            search: false,
        },
        {
            title: "反馈状态",
            dataIndex: "status",
            align: "center",
            search: true,
            renderFormItem: () => (
                <Select
                    allowClear
                    placeholder='请选择状态'
                    options={[
                        { label: "待处理", value: 'ENABLE' },
                        { label: "已处理", value: 'DISABLE' },
                    ]}
                />
            ),
            render: (text: string) => {
                switch (text) {
                    case "NONE":
                        return null;
                    case "ENABLE":
                        return <span style={{ color: 'orange' }}>待处理</span>;
                    case "DISABLE":
                        return <span style={{ color: 'green' }}>已处理</span>;
                    default:
                        return <span>未知</span>;
                }
            }
        },
        {
            title: "反馈时间",
            dataIndex: "createAt",
            align: "center",
            search: false,
            width: 300,
        },
        {
            title: "反馈时间",
            dataIndex: "createAt",
            align: "center",
            valueType: "dateRange",
            hideInTable: true,
        },
        {
            title: "操作",
            key: 'operation',
            search: false,
            align: "center",
            render: (_: any, record: any) => {
                if (record.status === 'ENABLE') {
                    return <Button style={{ color: '#58CCE0' }} type="text" onClick={() => changeStatus(record)}>处理</Button>;
                } else if (record.status === 'DISABLE') {
                    return <Button style={{ color: '#F3C017' }} type="text" onClick={() => changeStatus(record)}>取消处理</Button>;
                }

                return null;
            },
        }
    ];

    const changeStatus = async (item: any) => {
        const data: any = await jsonRequest.put(`/back/health-feedback/change-status/result/${item.id}`, {})
            .then((resp: any) => resp.json());
        if (data?.code === 0) {
            tableRef.current?.reload();
            message.success("处理成功");
        } else {
            message.error(data?.msg || "处理失败");
        }
    };

    const expandRequest = async (row: any) => {
        let result;
        if (row.businessType === 'PULSE') {
            result = await jsonRequest.get(`/back/other/pulse-detail/${row.businessId}`, {
            }).then((resp: any) => resp.json());
        } else if (row.businessType === 'FOOD_RECOGNITION') {
            result = await jsonRequest.get(`/back/check-record/food/${row.businessId}`, {
            }).then((resp: any) => resp.json());
        } else {
            result = await jsonRequest.get(`/back/check-record/${row.businessId}`, {
            }).then((resp: any) => resp.json());
        }

        const { data } = result;
        console.log("获取展示行的数据", data);
        setDataList(prevList => [...prevList, data]);
    }

    const request = async (params: any) => {
        const result: any = await jsonRequest.get("/back/health-feedback/page/result", {
            currentPage: params.current,
            pageSize: params.pageSize,
            queryBeginDate: params?.createAt?.[0],
            queryEndDate: params?.createAt?.[1],
            status: params?.status,
            likeFlag: params?.likeFlag,
            businessType: params?.businessType,
        }).then((resp: any) => resp.json());
        const { data } = result;
        return { data: data?.data, success: result.code === 0, total: data?.count };
    };

    const removePTags = (html: string) => {
        return html.replace(/<p>/g, '').replace(/<\/p>/g, '');
    }

    return <>
        <h1>结果反馈</h1>
        <ProTable
            rowKey="id"
            scroll={{ x: 100 }}
            actionRef={tableRef}
            cardBordered
            columns={columns}
            request={request}
            pagination={{ defaultPageSize: 10, showSizeChanger: true }}
            search={{ span: 6, defaultCollapsed: false }}
            expandable={{
                onExpand: async (expanded, record: any) => {
                    console.log(expanded, record);
                    record.expanded = expanded;
                    const idArray = dataList?.map(item => item?.id);
                    if (idArray.includes(record?.businessId)) {
                        console.log('展开行的数据已经存在', dataList);
                        console.log('id 存在于 idArray 中', record.businessId);
                    } else {
                        record.loading = true;
                        await expandRequest(record);
                        record.loading = false;
                    }
                },
                expandedRowRender: (row) => {
                    if (row.loading) {
                        return <Spin />
                    }
                    if (row.businessType === "PULSE") {
                        return (
                            <PulseResult
                                dataList={dataList}
                                id={row.businessId}
                            />
                        )
                    } else if (row.businessType === 'TONGUE') {
                        return (
                            <TongueDiagnosisResult
                                dataList={dataList}
                                row={row}
                                id={row.businessId}
                            />
                        )
                    } else if (row.businessType === 'CORPOREALITY') {
                        return (
                            <ConstitutionResult
                                removePTags={removePTags}
                                dataList={dataList}
                                row={row}
                                id={row.businessId}
                            />
                        )
                    } else if (row.businessType === 'FOOD_RECOGNITION') {
                        return (
                            <FoodRecognitionResult
                                dataList={dataList}
                                row={row}
                                id={row.businessId}
                            />
                        )
                    } else return <div>不支持的业务类型</div>
                },
            }}
        />
    </>;
}